import { fabric } from 'fabric';

// Disable toSVG entirely for security reasons (GHSA-hfvx-25r5-qc3w)
// See: https://github.com/advisories/GHSA-hfvx-25r5-qc3w
const toSvgError = () => {
  throw new Error(
    'Security Policy: toSVG() is disabled due to XSS vulnerability (GHSA-hfvx-25r5-qc3w).'
  );
};
fabric.StaticCanvas.prototype.toSVG = toSvgError;
fabric.Object.prototype.toSVG = toSvgError;
fabric.Object.prototype.getSvgCommons = toSvgError;
for (const objectTypeMaybe of Object.values(fabric)) {
  if (
    typeof objectTypeMaybe === 'function' &&
    Object.prototype.isPrototypeOf.call(
      fabric.Object.prototype,
      objectTypeMaybe.prototype
    )
  ) {
    const objectType = objectTypeMaybe as typeof fabric.Object;
    objectType.prototype.toSVG = toSvgError;
    objectType.prototype.getSvgCommons = toSvgError;
  }
}

fabric.Object.prototype.borderColor = '#65BBFB';
fabric.Object.prototype.cornerColor = 'white';
fabric.Object.prototype.cornerStrokeColor = '#65BBFB';
fabric.Object.prototype.transparentCorners = false;

// Drawn directly instead of from a cached bitmap: the cache of an object is
// capped at 2M pixels (fabric.perfLimitSizeTotal) for the whole object, even
// where it is off screen, so a drawing zoomed in showed pixelated. Annotations
// are few, and canvases cover only what is in view (see annotationLayout).
fabric.Object.prototype.objectCaching = false;

// Allow touch scrolling
// Backport of https://github.com/fabricjs/fabric.js/pull/10078
// See: https://github.com/fabricjs/fabric.js/issues/5903 for more context
(() => {
  Object.assign(fabric.Canvas.prototype, {
    _onTouchStart(this: fabric.Canvas, e: TouchEvent) {
      // we will prevent scrolling if allowTouchScrolling is not enabled and
      let shouldPreventScrolling = !this.allowTouchScrolling;
      const currentActiveObject = this._activeObject;
      if ((this as any).mainTouchId === null) {
        (this as any).mainTouchId = (this as any).getPointerId(e);
      }
      (this as any).__onMouseDown(e);
      // after executing fabric logic for mouse down let's see
      // if we didn't change target or if we are drawing
      // we want to prevent scrolling anyway
      if (
        (this as any).isShapeDrawingMode ||
        this.isDrawingMode ||
        (currentActiveObject && (this as any)._target === currentActiveObject)
      ) {
        shouldPreventScrolling = true;
      }
      // prevent default, will block scrolling from start
      if (shouldPreventScrolling) {
        e.preventDefault();
      }
      (this as any)._resetTransformEventData();
      const canvasElement = (this as any).upperCanvasEl;
      const eventTypePrefix = (this as any)._getEventPrefix();
      const doc = canvasElement.ownerDocument || null;
      fabric.util.addListener(
        doc,
        `touchend`,
        (this as any)._onTouchEnd as EventListener
      );
      // if we scroll don't register the touch move event
      if (shouldPreventScrolling) {
        fabric.util.addListener(
          doc,
          'touchmove',
          (this as any)._onMouseMove as EventListener
        );
      }
      // Unbind mousedown to prevent double triggers from touch devices
      fabric.util.removeListener(
        canvasElement,
        `${eventTypePrefix}down`,
        (this as any)._onMouseDown as EventListener
      );
    }
  });
})();

// Shadows sized with the canvas's own pixel ratio instead of fabric's global
// one: annotation canvases have a ratio of their own (see canvasPixelRatio).
// The same otherwise.
Object.assign(fabric.Object.prototype, {
  _setShadow(this: fabric.Object, ctx: CanvasRenderingContext2D) {
    const { canvas } = this;
    // Shadows set as strings are made Shadow instances by fabric
    const shadow = this.shadow as fabric.Shadow | undefined;
    if (!shadow) return;
    const retinaScaling: number = (canvas as any)?.getRetinaScaling?.() ?? 1;
    const multX = (canvas?.viewportTransform?.[0] || 1) * retinaScaling;
    const multY = (canvas?.viewportTransform?.[3] || 1) * retinaScaling;
    const scaling = shadow.nonScaling
      ? { scaleX: 1, scaleY: 1 }
      : this.getObjectScaling();
    ctx.shadowColor = shadow.color ?? '';
    ctx.shadowBlur =
      ((shadow.blur ?? 0) *
        (fabric as any).browserShadowBlurConstant *
        (multX + multY) *
        (scaling.scaleX + scaling.scaleY)) /
      4;
    ctx.shadowOffsetX = (shadow.offsetX ?? 0) * multX * scaling.scaleX;
    ctx.shadowOffsetY = (shadow.offsetY ?? 0) * multY * scaling.scaleY;
  }
});
